// snackbar.js — eine Snackbar zur Zeit (M3), weitere warten in der Schlange.
//   snackbar('Gespeichert');  snackbar({ text, action: { label, onClick }, error, duration, key })
// Gibt ein Promise zurück: 'action' | 'dismiss' | 'timeout' — Grundlage für Rückgängig mit verzögertem Senden.
// preempt: beendet eine gerade gezeigte reine Bestätigung (ohne Aktion, kein Fehler) — nie ein Rückgängig, dessen Ende sendet.
// badge: Material-Symbol in einem Abzeichen, das beim Erscheinen morpht (Erfolge). Wegwischen schließt ('dismiss').
import { reducedMotion } from '../core/dom.js';
import { swipeToDismiss, unlockReveal } from '../motion/motion.js';
import { t } from '../../shared/i18n.js';

const queue = [];
let showing = null;

function host() {
  let h = document.querySelector('.vc-snackbar-host');
  if (!h) {
    h = document.createElement('div');
    h.className = 'vc-snackbar-host';
    h.setAttribute('role', 'region');
    h.setAttribute('aria-label', t('ui.messages'));
    document.body.appendChild(h);
  }
  return h;
}

function next() {
  if (showing || !queue.length) return;
  const item = queue.shift();
  showing = item;
  const el = document.createElement('div');
  el.className = 'vc-snackbar' + (item.error ? ' is-error' : '');
  el.setAttribute('role', item.error ? 'alert' : 'status');
  let badge = null;
  if (item.badge) {
    badge = document.createElement('span');
    badge.className = 'vc-snackbar-badge'; badge.setAttribute('aria-hidden', 'true');
    const ic = document.createElement('span'); ic.className = 'vc-icon'; ic.textContent = item.badge;
    badge.appendChild(ic); el.appendChild(badge);
  }
  const txt = document.createElement('div');
  txt.className = 'vc-snackbar-text';
  txt.textContent = item.text;
  el.appendChild(txt);
  if (item.action) {
    const b = document.createElement('button');
    b.type = 'button'; b.className = 'vc-btn is-text'; b.textContent = item.action.label;
    b.addEventListener('click', () => close('action'));
    el.appendChild(b);
  }
  if (item.closable !== false && (item.action || item.duration === Infinity)) {
    const x = document.createElement('button');
    x.type = 'button'; x.className = 'vc-icon-btn'; x.setAttribute('aria-label', item.closeLabel || t('ui.close'));
    x.innerHTML = '<span class="vc-icon" aria-hidden="true">close</span>';
    x.addEventListener('click', () => close('dismiss'));
    el.appendChild(x);
  }
  host().appendChild(el);
  if (badge) unlockReveal([badge], { delay: 120 });
  const unswipe = swipeToDismiss(el, () => close('dismiss', true));
  let timer = null, left = item.duration, started = Date.now();
  const arm = () => { if (left !== Infinity) timer = setTimeout(() => close('timeout'), left); };
  const pause = () => { if (timer) { clearTimeout(timer); timer = null; left -= Date.now() - started; } };
  const resume = () => { started = Date.now(); if (!timer) arm(); };
  el.addEventListener('pointerenter', pause); el.addEventListener('pointerleave', resume);
  el.addEventListener('focusin', pause); el.addEventListener('focusout', resume);
  arm();
  function close(reason, swiped = false) {
    if (item.done) return;
    item.done = true;
    clearTimeout(timer);
    unswipe();
    if (reason === 'action' && item.action.onClick) { try { item.action.onClick(); } catch (e) { console.error(e); } }
    item.resolve(reason);
    const finish = () => { el.remove(); showing = null; next(); };
    if (reducedMotion() || swiped) return finish(); // weggewischt: schon hinausgeflogen
    el.classList.add('is-leaving');
    el.addEventListener('animationend', finish, { once: true });
    setTimeout(finish, 400);
  }
  item.close = close;
}

export function snackbar(opts) {
  const o = typeof opts === 'string' ? { text: opts } : { ...opts };
  o.duration = o.duration ?? (o.action ? 6000 : 4000);
  return new Promise((resolve) => {
    o.resolve = resolve;
    if (o.key) {
      for (let i = queue.length - 1; i >= 0; i--) if (queue[i].key === o.key) { queue[i].resolve('replaced'); queue.splice(i, 1); }
      if (showing && showing.key === o.key) showing.close('replaced');
    }
    queue.push(o);
    if (o.preempt && showing && !showing.action && !showing.error && !showing.done) showing.close('replaced');
    next();
  });
}

/** Rückgängig mit verzögertem Senden: run() läuft erst, wenn die Snackbar ohne „Rückgängig“ endet. */
export async function withUndo(text, run, { undoLabel = t('ui.undo'), duration = 6000 } = {}) {
  let sent = false;
  const flush = () => { if (!sent) { sent = true; run(); } };
  const onHide = () => flush();
  addEventListener('pagehide', onHide, { once: true });
  const r = await snackbar({ text, action: { label: undoLabel }, duration });
  removeEventListener('pagehide', onHide);
  if (r === 'action') return false;
  flush();
  return true;
}
